"use client";

import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import {
  AppRoutesEnum,
  CtaNameEnum,
  EventNamesEnum,
  URLQueryParamsEnum,
} from "@/constants/constants";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDialect } from "@/hooks/useDialect";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import {
  addQueryParamsToURL,
  captureError,
  cn,
  generateGenreDetailUrl,
  getDefaultDialect,
} from "@/utils/common.helpers";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDownSmallIcon } from "./Icons";
import { Text } from "./Text";

interface GenreItem {
  genreId: number;
  genreName: string;
}

export const CategoriesDropdown = () => {
  const { t, currentLanguage } = useClientTranslations();
  const { currentDialect } = useDialect();
  const { trackEvent } = useTrackingAnalytics();
  const pathname = usePathname();
  const isCommonHome = pathname === AppRoutesEnum.COMMON_HOME;
  const targetDialect = isCommonHome
    ? getDefaultDialect().toLowerCase()
    : currentDialect.urlValue;
  const isActive = pathname?.includes("/genre");
  const [genres, setGenres] = useState<GenreItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    const fetchGenres = async () => {
      try {
        setIsLoading(true);
        const response = await apiSdkInstance.content.getAllGenresGrouped({
          lang: currentLanguage,
          dialect: targetDialect,
        });

        if (isCancelled) return;

        if (!("error" in response) && response.genreWiseData.length > 0) {
          setGenres(
            response.genreWiseData
              .filter(
                (genre) => genre.movies.length > 0 || genre.shows.length > 0,
              )
              .map(({ genreId, genreName }) => ({ genreId, genreName })),
          );
        }
      } catch (error) {
        captureError(error, "CategoriesDropdown: Failed to fetch genres");
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchGenres();
    return () => {
      isCancelled = true;
    };
  }, [currentLanguage, targetDialect]);

  const genreNames = useMemo(() => genres.map((g) => g.genreName), [genres]);

  const getGenreUrl = useCallback(
    (genre: GenreItem) =>
      addQueryParamsToURL(
        generateGenreDetailUrl({
          lang: currentLanguage,
          dialect: targetDialect,
          slug: genre.genreName.toLowerCase().replace(/\s+/g, "-"),
          id: genre.genreId,
        }),
        isCommonHome
          ? { [URLQueryParamsEnum.SHOW_USER_CULTURE_POPUP]: true }
          : {},
      ),
    [currentLanguage, isCommonHome, targetDialect],
  );

  const handleDropdownOpen = useCallback(() => {
    setIsOpen(true);
    if (genreNames.length > 0) {
      trackEvent({
        eventName: EventNamesEnum.CATEGORY_DROPDOWN_VIEWED,
        properties: {
          category_list: genreNames,
        },
      });
    }
  }, [genreNames, trackEvent]);

  const handleCategoryClick = useCallback(
    (genre: GenreItem) => {
      trackEvent({
        eventName: EventNamesEnum.CATEGORY_CLICKED,
        properties: {
          cta_name: CtaNameEnum.CATEGORY_SELECTED,
          current_value: genre.genreName,
          category_list: genreNames,
        },
      });
      setIsOpen(false);
    },
    [genreNames, trackEvent],
  );

  return (
    <div
      className="relative"
      onMouseEnter={handleDropdownOpen}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Trigger Button */}
      <button
        className={cn(
          "flex items-center gap-1 rounded-full px-5 py-2 text-sm font-medium transition-all duration-200",
          isActive
            ? "bg-white text-stg-grey-2"
            : "text-white hover:text-white/80",
        )}
        data-testid="categories-dropdown-trigger"
      >
        <span className="text-sm font-medium">{t("common.categories")}</span>
        <ChevronDownSmallIcon
          className={cn(
            "h-5 w-5 text-current transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 top-full z-[1] translate-x-[8%] pt-3"
          data-testid="categories-dropdown-menu"
        >
          {/* Invisible bridge to maintain hover */}
          <div className="absolute left-0 top-0 h-2 w-full" />
          <div className="rounded-lg border border-stg-grey-3 bg-stg-categories-dropdown-background p-6 backdrop-blur-lg">
            {isLoading ? (
              <div className="flex min-h-32 min-w-[36rem] items-center justify-center">
                <Text size={3} appearance="secondary">
                  {t("common.loading")}
                </Text>
              </div>
            ) : genres.length > 0 ? (
              <div className="grid min-w-[36rem] grid-cols-4 gap-x-4 gap-y-1">
                {genres.map((genre) => (
                  <GenreLink
                    key={genre.genreId}
                    genre={genre}
                    href={getGenreUrl(genre)}
                    onClick={() => handleCategoryClick(genre)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex min-h-32 min-w-[36rem] items-center justify-center">
                <Text size={3} appearance="secondary">
                  {t("common.noContentAvailable")}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const GenreLink = ({
  genre,
  href,
  onClick,
}: {
  genre: GenreItem;
  href: string;
  onClick: () => void;
}) => (
  <Link
    href={href}
    onClick={onClick}
    className="block whitespace-nowrap rounded px-4 py-2 font-medium text-stg-grey-6 transition-colors hover:bg-white hover:text-black"
  >
    {genre.genreName}
  </Link>
);

export default CategoriesDropdown;
